import React, { forwardRef, useId } from 'react';

import { ICON_PATHS } from './Icon.registry';
import type { IconProps, IconSizeToken } from './Icon.types';

const SIZE_TOKENS: Record<IconSizeToken, number> = {
  sm: 16,
  md: 24,
  lg: 32,
};

const ICON_COORDINATE_SCALE = 0.75;

function resolveSize(size: IconProps['size']) {
  return SIZE_TOKENS[size ?? 'md'];
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { 'aria-label': ariaLabel, color, name, role, size, style, title, ...props },
  ref,
) {
  const pathData = ICON_PATHS[name as keyof typeof ICON_PATHS];
  const titleId = useId();

  if (!pathData) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[Icon] Unknown icon name: "${name}"`);
    }

    return null;
  }

  const resolvedSize = resolveSize(size);
  const isLabelled = Boolean(title || ariaLabel);

  return (
    <svg
      {...props}
      aria-hidden={isLabelled ? undefined : true}
      aria-label={ariaLabel}
      aria-labelledby={title && !ariaLabel ? titleId : undefined}
      fill="currentColor"
      focusable={false}
      height={resolvedSize}
      ref={ref}
      role={isLabelled ? role ?? 'img' : undefined}
      style={{ ...style, ...(color ? { color } : undefined) }}
      viewBox="0 0 24 24"
      width={resolvedSize}
      xmlns="http://www.w3.org/2000/svg"
    >
      {title ? <title id={titleId}>{title}</title> : null}
      <g transform={`scale(${ICON_COORDINATE_SCALE})`}>
        {pathData.map((path) => (
          <path d={path} key={path} />
        ))}
      </g>
    </svg>
  );
});

Icon.displayName = 'Icon';
